<script setup>
import AppIcon from 'module/icon/AppIcon.vue';
import { userWatchlist } from 'app/userWatchlist';
import { onBeforeMount, ref } from 'vue';
import { trackGa4 } from 'util/googleAnalytics';

const props = defineProps({
    jobId: { type: String, required: true },
    buttonStyle: { type: Boolean, default: false },
    smallButton: { type: Boolean, default: false },
    isFetched: { type: Boolean, default: false },
    iconOnly: { type: Boolean, default: false },
    stickyHeader: { type: Boolean, default: false },
    event: { type: String, default: '' },
    label: { type: String, default: '' },
    labelWatched: { type: String, default: '' },
    labelTablet: { type: Boolean, default: false },
    isJobHeaderSplitview: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
});

const isWatched = ref(false);
const disabled = ref(props.disabled);

function update() {
    isWatched.value = userWatchlist.isWatched(props.jobId);
    disabled.value = isWatched.value && props.disabled ? false : props.disabled;
}

onBeforeMount(() => {
    if (!props.isFetched) {
        userWatchlist.fetchAll();
    }

    userWatchlist.events.on('onResponse', update);
    userWatchlist.events.on('onAddJob', update);
    userWatchlist.events.on('onRemoveJob', update);
});

const moduleElement = ref(null);
function eventClicked() {
    const eventCategory = document.documentElement.getAttribute('data-event-category');
    const routeName = document.documentElement.getAttribute('data-route-name');
    let prefix = '';

    if (eventCategory === 'page: index') {
        prefix = 'HOME';
    } else if (eventCategory === 'page: job') {
        prefix = 'JOBD';
    } else if (eventCategory === 'page: jobs') {
        prefix = 'JOBS';
    } else if (eventCategory === 'page: jobalarm') {
        prefix = 'JA';
    } else if (eventCategory === 'page: jobmatching') {
        prefix = 'JM';
    } else if (eventCategory === 'page: watchlist') {
        prefix = 'WL';
    } else if (eventCategory === 'page: company-follow') {
        prefix = 'CA';
    } else if ((eventCategory === 'page: businesspage_ep')
        || (eventCategory === 'page: businesspage_epp')
        || (eventCategory === 'page: businesspage_bs')) {
        if (routeName === 'brandingSolution.index') {
            prefix = 'COMP';
        } else if (routeName === 'brandingSolution.jobs') {
            prefix = 'COMP_JA';
        }
    }

    if (userWatchlist.isWatched(props.jobId)) {
        userWatchlist.removeJob(props.jobId);

        trackGa4({
            event: 'click_element',
            element: `${prefix}: Watchlist Remove`,
            element_detail: props.jobId || null,
        });
    } else if (!props.disabled) {
        userWatchlist.addJob(props.jobId, moduleElement.value);

        trackGa4({
            event: 'click_element',
            element: `${prefix}: Watchlist Add`,
            element_detail: props.jobId || null,
        });
    }
}
</script>

<template>
    <div
        ref="moduleElement"
        class="m-watchlistButton"
    >
        <button
            :id="`watchlist-button-${jobId}`"
            :class="[
                'm-watchlistButton__button',
                buttonStyle ? 'm-watchlistButton__button--buttonStyle' : 'm-watchlistButton__button--linkStyle',
                smallButton ? 'm-watchlistButton__button--small' : '',
                isFetched ? 'm-watchlistButton__button--fetched' : '',
                isWatched ? 'm-watchlistButton__button--watched' : '',
                iconOnly ? 'm-watchlistButton__button--iconOnly' : '',
            ]"
            :data-job-id="jobId"
            :data-event="event || null"
            :disabled="disabled"
            :data-fetched="isFetched ? 'true' : null"
            :aria-label="isWatched ? 'Von der Merkliste entfernen' : 'Auf Merkliste setzen'"
            @click.stop.prevent="eventClicked"
        >
            <AppIcon
                :name="isWatched ? 'star-filled' : 'star'"
                size="small"
                :icon-variant="smallButton ? 's' : 'm'"
            />

            <template v-if="!iconOnly">
                <span
                    :class="[
                        'm-watchlistButton__label',
                        !buttonStyle ? 'm-watchlistButton__label--green' : '',
                        labelTablet ? 'm-watchlistButton__label--labelTablet' : '',
                        isJobHeaderSplitview ? 'm-watchlistButton__label--isJobHeaderSplitview' : '',
                    ]"
                    :data-label="label"
                    :data-label-watched="labelWatched"
                    :aria-hidden="isWatched ? 'true' : null"
                >
                    {{ isWatched ? labelWatched : label }}
                </span>
            </template>
        </button>
    </div>
</template>

<style lang="scss">
@use "styles";
</style>
